import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Label,
} from '@scio3/ui';
import { reauthenticateWithCredential, updatePassword, EmailAuthProvider } from 'firebase/auth';
import {
  auth,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  type MultiFactorResolver,
  type MultiFactorError,
} from '@/lib/firebase';
import { useMutation } from '@apollo/client/react';
import { UPDATE_PASSWORD_CHANGE_REQUIRE } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';

// ---------------------------------------------------------------------------
// Schema — like createChangePasswordSchema but without currentPassword
// ---------------------------------------------------------------------------

function createForcePasswordSchema(enforceComplexity: boolean) {
  const newPasswordSchema = enforceComplexity
    ? z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(48, 'Password must be at most 48 characters')
        .regex(/[a-z]/, 'Password must contain a lowercase letter (a-z)')
        .regex(/[A-Z]/, 'Password must contain an uppercase letter (A-Z)')
        .regex(/[0-9]/, 'Password must contain a number (0-9)')
        .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain a symbol (!@#$%...)')
    : z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(48, 'Password must be at most 48 characters');

  return z
    .object({
      currentPassword: z
        .string()
        .min(1, 'Current password is required')
        .max(48, 'Password must be at most 48 characters'),
      newPassword: newPasswordSchema,
      confirmPassword: z
        .string()
        .min(1, 'Confirm password is required')
        .max(48, 'Password must be at most 48 characters'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: 'Confirm password does not match',
      path: ['confirmPassword'],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message: 'New password must be different from current password',
      path: ['newPassword'],
    });
}

type ForcePasswordFormValues = z.infer<ReturnType<typeof createForcePasswordSchema>>;

type Step = 'password' | 'mfa-challenge';
type MfaMethod = 'totp' | 'phone';

// ---------------------------------------------------------------------------
// ForcePasswordChangeDialog — blocks app until password is changed
// ---------------------------------------------------------------------------

export function ForcePasswordChangeDialog() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);

  const enforceComplexity = company?.feature?.enforcePasswordComplexity ?? false;
  const schema = useMemo(() => createForcePasswordSchema(enforceComplexity), [enforceComplexity]);

  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>('password');
  const [otpCode, setOtpCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState<MfaMethod>('totp');
  const [smsVerificationId, setSmsVerificationId] = useState<string | null>(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);

  const newPasswordRef = useRef('');
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  const [updatePasswordChangeRequire] = useMutation(UPDATE_PASSWORD_CHANGE_REQUIRE);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForcePasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const userName = me ? `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() : '';

  // SMS countdown timer
  useEffect(() => {
    if (smsCountdown <= 0) return;
    const timer = setTimeout(() => setSmsCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [smsCountdown]);

  // Auto-send SMS when transitioning to mfa-challenge with phone method
  useEffect(() => {
    if (!resolver || selectedMfaMethod !== 'phone' || smsVerificationId || smsSending) return;
    if (step !== 'mfa-challenge') return;
    sendSmsCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver, selectedMfaMethod, step]);

  const sendSmsCode = useCallback(async () => {
    if (!resolver || smsCountdown > 0 || smsSending) return;
    setSmsSending(true);
    setMfaError('');

    try {
      if (!recaptchaVerifierRef.current && recaptchaContainerRef.current) {
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
          size: 'invisible',
        });
      }

      const phoneHint = resolver.hints.find((h) => h.factorId === 'phone');
      if (!phoneHint || !recaptchaVerifierRef.current) return;

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        recaptchaVerifierRef.current,
      );
      setSmsVerificationId(verId);
      setSmsCountdown(60);
    } catch (err) {
      const firebaseErr = err as { code?: string };
      if (
        firebaseErr.code === 'auth/captcha-check-failed' ||
        firebaseErr.code === 'auth/invalid-app-credential'
      ) {
        if (recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current.clear();
          recaptchaVerifierRef.current = null;
        }
        setMfaError(t('reCAPTCHA verification failed. Please try again.'));
      } else {
        setMfaError(t('Failed to send SMS verification code.'));
      }
    } finally {
      setSmsSending(false);
    }
  }, [resolver, smsCountdown, smsSending, t]);

  const hasTotpHint = resolver?.hints.some((h) => h.factorId === 'totp') ?? false;
  const hasSmsHint = resolver?.hints.some((h) => h.factorId === 'phone') ?? false;

  async function finishPasswordChange(newPassword: string) {
    const user = auth.currentUser;
    if (!user) return;

    await updatePassword(user, newPassword);

    // Clear the passwordChangeRequire flag on backend
    await updatePasswordChangeRequire();

    toast.success(t('Your new password has been set!'));
    window.location.reload();
  }

  async function onSubmit(data: ForcePasswordFormValues) {
    const user = auth.currentUser;
    if (!user?.email) return;

    setSubmitting(true);
    try {
      // Reauthenticate with current (admin-set) password to satisfy Firebase's recent-login requirement
      const credential = EmailAuthProvider.credential(user.email, data.currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Now update to the new password
      await updatePassword(user, data.newPassword);

      // Clear the passwordChangeRequire flag on backend
      await updatePasswordChangeRequire();

      toast.success(t('Your new password has been set!'));
      // Reload to re-run full auth flow (ME_QUERY will return without passwordChangeRequire)
      window.location.reload();
    } catch (error) {
      const firebaseError = error as { code?: string };

      if (firebaseError.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = getMultiFactorResolver(auth, error as MultiFactorError);
        setResolver(mfaResolver);
        newPasswordRef.current = data.newPassword;

        const preferTotp = mfaResolver.hints.some((h) => h.factorId === 'totp');
        setSelectedMfaMethod(preferTotp ? 'totp' : 'phone');
        setStep('mfa-challenge');
      } else if (
        firebaseError.code === 'auth/wrong-password' ||
        firebaseError.code === 'auth/invalid-credential'
      ) {
        setError('currentPassword', {
          message: t('Current password is incorrect'),
        });
      } else if (firebaseError.code === 'auth/requires-recent-login') {
        toast.error(t('Please log in again to change your password'));
      } else {
        console.error('Force password change error:', firebaseError.code, error);
        toast.error(t('Failed to set new password.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!resolver) return;

    setMfaError('');
    setSubmitting(true);

    try {
      let assertion;
      if (selectedMfaMethod === 'totp') {
        const hint = resolver.hints.find((h) => h.factorId === 'totp') ?? resolver.hints[0];
        assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otpCode);
      } else {
        if (!smsVerificationId) {
          setMfaError(t('SMS verification not started. Please resend the code.'));
          setSubmitting(false);
          return;
        }
        const cred = PhoneAuthProvider.credential(smsVerificationId, otpCode);
        assertion = PhoneMultiFactorGenerator.assertion(cred);
      }

      await resolver.resolveSignIn(assertion);
      await auth.currentUser?.getIdToken(true);

      await finishPasswordChange(newPasswordRef.current);
    } catch (err) {
      const firebaseErr = err as { code?: string };
      if (firebaseErr.code === 'auth/invalid-verification-code') {
        setMfaError(t('Code is invalid.'));
      } else if (
        firebaseErr.code === 'auth/totp-challenge-timeout' ||
        firebaseErr.code === 'auth/token-expired'
      ) {
        setMfaError(t('Code is expired.'));
      } else {
        console.error('MFA verify error:', firebaseErr.code, err);
        setMfaError(t('An error occurred. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleSwitchMethod() {
    setSelectedMfaMethod(selectedMfaMethod === 'totp' ? 'phone' : 'totp');
    setOtpCode('');
    setMfaError('');
  }

  return (
    <AlertDialog open>
      <AlertDialogContent
        className="max-w-md"
        data-testid="force-password-change-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div ref={recaptchaContainerRef} id="force-password-recaptcha" />

        {step === 'password' && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {userName ? `${t('Welcome')}, ${userName}!` : `${t('Welcome')}!`}
              </AlertDialogTitle>
            </AlertDialogHeader>

            <p className="text-sm text-muted-foreground">{t('Create your new password')}</p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="force-current-password">{t('Current Password')}</Label>
                <Input
                  id="force-current-password"
                  data-testid="force-current-password"
                  type="password"
                  className="ph-no-capture"
                  autoComplete="current-password"
                  autoFocus
                  {...register('currentPassword')}
                />
                {errors.currentPassword && (
                  <p className="text-sm text-destructive">{t(errors.currentPassword.message!)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="force-new-password">{t('New Password')}</Label>
                <Input
                  id="force-new-password"
                  data-testid="force-new-password"
                  type="password"
                  className="ph-no-capture"
                  autoComplete="new-password"
                  {...register('newPassword')}
                />
                {errors.newPassword && (
                  <p className="text-sm text-destructive">{t(errors.newPassword.message!)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="force-confirm-password">{t('Confirm Password')}</Label>
                <Input
                  id="force-confirm-password"
                  data-testid="force-confirm-password"
                  type="password"
                  className="ph-no-capture"
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{t(errors.confirmPassword.message!)}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('Create New Password')}
              </Button>
            </form>
          </>
        )}

        {step === 'mfa-challenge' && (
          <form onSubmit={handleMfaVerify}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('Two-Factor Authentication')}</AlertDialogTitle>
            </AlertDialogHeader>
            <p className="mt-2 text-sm text-muted-foreground">
              {selectedMfaMethod === 'totp'
                ? t('Enter the 6-digit code from your authenticator app.')
                : smsSending
                  ? t('Sending SMS verification code...')
                  : t('Enter the 6-digit code sent to your phone.')}
            </p>

            <div className="mt-4 space-y-4">
              {mfaError && <p className="text-sm text-destructive">{mfaError}</p>}

              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} autoFocus>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {selectedMfaMethod === 'phone' && (
                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={sendSmsCode}
                    disabled={smsCountdown > 0 || smsSending}
                  >
                    {smsCountdown > 0
                      ? t('Resend SMS ({{seconds}}s)', { seconds: smsCountdown })
                      : t('Resend SMS')}
                  </Button>
                </div>
              )}

              {hasTotpHint && hasSmsHint && (
                <div className="text-center">
                  <Button type="button" variant="link" size="sm" onClick={handleSwitchMethod}>
                    {selectedMfaMethod === 'totp'
                      ? t('Use SMS Verification')
                      : t('Use Authenticator App')}
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || otpCode.length !== 6}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('Verify')}
              </Button>
            </div>
          </form>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
